import { Component, ChangeDetectorRef, OnDestroy, OnInit } from "@angular/core";
import { MenuItem } from "primeng/api";
import { CommonModule, NgOptimizedImage } from "@angular/common";

import { RouterModule, Router } from "@angular/router";
import { PrimeNGSharedModule } from "@shared/prime/prime-ng.module";
import { NotificationPopupComponent } from "./notification-popup/notification-popup.component";
import { BadgeModule } from 'primeng/badge';
import { AuthService } from "@core/services/auth/auth.service";
import { TimerComponent } from "./timer/timer.component";
import { NotificationService, NotificationItem } from "@core/services/api/notification.service";
import { Subscription, interval, switchMap, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SessionWarningDialogComponent } from "../session-warning-dialog/session-warning-dialog.component";
import { HeaderService, SearchResult } from "@core/services/api/header.service";
import { SearchPopupComponent } from "./search-popup/search-popup.component";

@Component({
  selector: "app-header",
  standalone: true,
  imports: [
    RouterModule,
    NgOptimizedImage,
    PrimeNGSharedModule,
    NotificationPopupComponent,
    CommonModule,
    BadgeModule,
    DialogModule,
    TimerComponent,
    SessionWarningDialogComponent,
    SearchPopupComponent
],
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
})
export class HeaderComponent implements OnInit, OnDestroy {
  constructor(
    private readonly authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private readonly notifications: NotificationService,
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer,
    private readonly headerApi: HeaderService
    ,
    private readonly router: Router
  ) {}

  showNotificationPopup = false;
  notificationCount: number = 0;
  notificationsList: NotificationItem[] = [];

  // Search state
  showSearchPopup = false;
  searchQuery = '';
  searchResults: SearchResult[] = [];
  isSearchLoading = false;
  searchTotalResults = 0;
  private readonly searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  // Help PDF preview state
  displayPdfViewer: boolean = false;
  pdfUrl: string | null = null;
  pdfFileName: string = 'Help Document';
  safePdfUrl: SafeResourceUrl | null = null;

  // Support dialog state
  showSupportDialog: boolean = false;
  // Backwards compatible single default email (kept in case other code references it); primary list is supportEmails
  supportMessage: string = 'Please contact your respective Bayer Technical Assistance Center for support.';
  supportEmails: Array<{ label: string; email: string }> = [];

  // Map for role pattern -> emails (adjust real addresses as needed)
  private readonly SUPPORT_EMAIL_MAP: { matcher: RegExp; entries: Array<{ label: string; email: string }> }[] = [
    { matcher: /RA Admin(?!.*Global)(?!.*Local)/i, entries: [ { label: 'RA Admin Support', email: 'support_ria@bayer.com' } ] },
    { matcher: /Global RA/i, entries: [ { label: 'Global RA Support', email: 'support_ria@bayer.com' } ] },
    { matcher: /Local RA/i, entries: [ { label: 'Local RA Support', email: 'support_ria@bayer.com' } ] }
  ];

  private unreadCountSub?: Subscription;
  private popupRefreshSub?: Subscription;
  private openPopupSub?: Subscription;

  // Navigation links built from permissions
  navLinks: Array<{ label: string; route: any[]; exact?: boolean }> = [];
  mobileMenuOpen = false;

  // User display info
  displayName: string = '';
  userRole: string = '';

  toggleNotifications(): void {
    this.showNotificationPopup = !this.showNotificationPopup;
    if (this.showNotificationPopup) {
      // Close search if open
      if (this.showSearchPopup) {
        this.showSearchPopup = false;
      }
      // Load immediately and start polling every 5 minutes
      this.fetchNotifications();
      this.startPopupPolling();
    } else {
      this.stopPopupPolling();
    }
    this.cdr.detectChanges(); 
  }

  toggleSearch(): void {
    this.showSearchPopup = !this.showSearchPopup;
    if (this.showSearchPopup) {
      // Close notifications if open
      if (this.showNotificationPopup) {
        this.showNotificationPopup = false;
        this.stopPopupPolling();
      }
      // Reset search state
      this.searchQuery = '';
      this.searchResults = [];
      this.searchTotalResults = 0;
    }
    this.cdr.detectChanges();
  }

  handleSearchClose(): void {
    this.showSearchPopup = false;
    this.searchQuery = '';
    this.searchResults = [];
    this.searchTotalResults = 0;
    this.cdr.detectChanges();
  }

  handleSearchChange(query: string): void {
    this.searchQuery = query;
    this.searchSubject.next(query);
  }

  handleSearchResultClick(result: SearchResult): void {
    this.handleSearchClose();
    
    // Navigate based on result type
    if (result.type === 'user' && result.user_id) {
      // Navigate to user details page
      this.router.navigate(['/user-details', result.user_id]);
    } else if ((result.type === 'rca' || result.type === 'response') && result.rca_id) {
      // For both RCA and responses, navigate to the RCA detail page
      // Response details should be accessible from the RCA page
      
      // First navigate away to force reload, then navigate to target
      this.router.navigateByUrl('/manage-assessment').then(() => {
        this.router.navigate(['/manage-assessment', result.rca_id], {
          queryParams: { fromAction: true }
        }).catch(() => {});
      }).catch(() => {});
    }
  }

  private performSearch(query: string): void {
    if (!query || query.trim().length < 2) {
      this.searchResults = [];
      this.searchTotalResults = 0;
      this.isSearchLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.isSearchLoading = true;
    this.cdr.detectChanges();

    this.headerApi.globalSearch(query.trim(), 5).subscribe({
      next: (res) => {
        this.searchResults = res.results || [];
        this.searchTotalResults = res.total_results || 0;
        this.isSearchLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.searchResults = [];
        this.searchTotalResults = 0;
        this.isSearchLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  handlePopupClose(): void {
    this.showNotificationPopup = false;
    this.stopPopupPolling();
    this.cdr.detectChanges();
  }

  private fetchNotifications(): void {
    this.notifications.getNotifications(1, 50, false).subscribe({
      next: (res) => {
        this.notificationsList = this.showNotificationBasedOnread(res.notifications || []);
        this.cdr.detectChanges();
      }
    });
  }

  showNotificationBasedOnread(data:any){
    const unread = data.filter((item:any)=> item.is_read === false);
    const read = data.filter((item:any)=> item.is_read === true);
    return [...unread, ...read];
  }

  private startPopupPolling(): void {
    this.popupRefreshSub?.unsubscribe();
    this.popupRefreshSub = interval(300000) // 5 minutes
      .pipe(switchMap(() => this.notifications.getNotifications(1, 10, false)))
      .subscribe((res) => {
        this.notificationsList = res.notifications || [];
        if (typeof res.unread_count === 'number') this.notificationCount = res.unread_count;
        this.cdr.detectChanges();
      });
  }

  private stopPopupPolling(): void {
    this.popupRefreshSub?.unsubscribe();
    this.popupRefreshSub = undefined;
  }

  private startUnreadCountPolling(): void {
    this.unreadCountSub?.unsubscribe();
    this.unreadCountSub = interval(300000) // 5 minutes
      .pipe(switchMap(() => this.notifications.getUnreadCount()))
      .subscribe((res) => {
        if (typeof res.unread_count === 'number') this.notificationCount = res.unread_count;
        this.cdr.detectChanges();
      });
  }

  private stopUnreadCountPolling(): void {
    this.unreadCountSub?.unsubscribe();
    this.unreadCountSub = undefined;
  }

  onMarkRead = (id: number) => {
    // optimistic UI: remove locally, then call API
    this.notificationsList = this.notificationsList.filter(n => n.id !== id);
    this.cdr.detectChanges();
    this.notifications.markRead(id).subscribe({
      next: () => { this.fetchNotifications(); },
    });
  }

  onMarkAllRead = () => {
    this.notifications.markAllRead().subscribe({
      next: () => {
        this.fetchNotifications();
      }
    });
  }

  items: MenuItem[] = [];
  logout(): void { this.authService.clearStorage(); }

  ngOnInit(): void {
    this.initUserMeta();
    this.buildMenuItems();
    this.buildNav();

    // subscribe to shared unread count stream for instant updates
    this.notifications.unreadCount$.subscribe((cnt) => {
      this.notificationCount = cnt;
      this.cdr.detectChanges();
    });

    // initial unread count and start polling
    this.notifications.getUnreadCount().subscribe();
    this.startUnreadCountPolling();

    // listen for cross-app open popup requests
    this.openPopupSub = this.notifications.openPopup$.subscribe(() => {
      if (!this.showNotificationPopup) {
        this.showNotificationPopup = true;
        this.fetchNotifications();
        this.startPopupPolling();
        this.cdr.detectChanges();
      }
    });

    // Setup search debouncing
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((query) => {
        this.performSearch(query);
      });
  }

  ngOnDestroy(): void { 
    this.stopUnreadCountPolling(); 
    this.stopPopupPolling(); 
    this.openPopupSub?.unsubscribe();
    this.searchSubscription?.unsubscribe();
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.cdr.detectChanges();
  }

  closeMobileMenu(): void {
    if (this.mobileMenuOpen) {
      this.mobileMenuOpen = false;
      this.cdr.detectChanges();
    }
  }

  // Fetch help document and open PDF dialog via HeaderService
  onHelpClick(): void {
    this.headerApi.getHelpDocument().subscribe({
      next: (res) => {
        const name = res?.document_title || res?.file_name || 'Help Document';
        const signed = res?.signed_url || '';
        if (!signed) return;
        this.pdfFileName = name;
        this.pdfUrl = signed;
        this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(signed);
        this.displayPdfViewer = true;
        this.cdr.detectChanges();
      },
      error: () => {
        // Optionally: show a toast
      }
    });
  }

  openSupport(): void { this.showSupportDialog = true; this.cdr.detectChanges(); }
  closeSupport(): void { this.showSupportDialog = false; this.cdr.detectChanges(); }
  copySupportEmail(email: string): void {
    // Fire and forget; lint prefers handling promise explicitly
    navigator.clipboard.writeText(email).catch(() => {});
  }
  openMail(email: string): void { window.location.href = `mailto:${email}`; }

  private initUserMeta(): void {
    const profile: any = this.authService.getProfile?.() || {};
    const first = typeof profile.firstName === 'string' ? profile.firstName.trim() : '';
    const last = typeof profile.lastName === 'string' ? profile.lastName.trim() : '';
    const email: string | undefined = typeof profile.email === 'string' ? profile.email : undefined;
    this.userRole = typeof profile.userRole === 'string' ? profile.userRole : '';

    if (first || last) {
      this.displayName = `${first} ${last}`.trim();
    } else if (email) {
      const local = email.split('@')[0];
      const parts = local.split(/[._-]+/).filter(Boolean);
      this.displayName = parts.map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
    } else {
      this.displayName = 'User';
    }

    this.buildSupportEmails();
  }

  private buildSupportEmails(): void {
    const role = this.userRole || '';
    const collected: { label: string; email: string }[] = [];
    this.SUPPORT_EMAIL_MAP.forEach(cfg => {
      if (cfg.matcher.test(role)) {
        cfg.entries.forEach(e => {
          if (!collected.some(c => c.email.toLowerCase() === e.email.toLowerCase())) {
            collected.push(e);
          }
        });
      }
    });
    // Fallback default if nothing matched
    if (!collected.length) {
      collected.push({ label: 'Support', email: 'ria-support@bayer.com' });
    }
    this.supportEmails = collected;
  }

  private buildMenuItems(): void {
    const headingHtml = `
      <div class="menu-header">
        <span class="menu-name">${this.displayName}</span>
        <div>
          <span class="menu-role">${this.userRole || ''}</span>
        </div>
      </div>
    `;

    this.items = [
      { label: headingHtml, escape: false },
      { separator: true },
  { label: 'Profile Settings', icon: 'pi pi-cog', command: () => { this.router.navigate(['/profile-settings']).catch(() => {}); } },
      { label: 'Help', icon: 'pi pi-info-circle', command: () => this.onHelpClick() },
      { label: 'Support', icon: 'pi pi-envelope', command: () => this.openSupport() },
      { separator: true },
      { label: 'Logout', icon: 'pi pi-sign-out', command: () => this.logout() },
    ];
  }

  private buildNav(): void {
    const perms = (this.authService.getPermissions?.() || []) as string[];

    const has = (p: string) => perms.includes(p);
    const any = (...ps: string[]) => ps.some(has);

    const showAssessment = any('Manage RCA', 'View RCA & Manage Response');
    // Show User Management only for RA Admin (or if explicit user perms exist)
    const showUserMgmt = has('View RA Admin Dashboard') || any('View User', 'Manage User');
    const showAuditLogs = any('View Local RA Audit Logs', 'View Global RA Audit Logs', 'View RA Admin Audit Logs');
    const showPermSettings = any('View Roles and Permissions', 'Manage Roles and Permissions');
    const showConfiguration = any('View Configuration', 'Manage Configuration');

    const links: Array<{ label: string; route: any[]; exact?: boolean }> = [];
    // Home always
    links.push({ label: 'Home', route: ['/home'], exact: true });
    // Assessment / Manage Assessment
    if (showAssessment) {
      links.push({ label: this.userRole.startsWith('Local RA') ? 'Assessment' : 'Manage Assessment', route: ['/manage-assessment'] });
    }
    if (showUserMgmt) {
      links.push({ label: 'User Management', route: ['/user-management'] });
    }
    if (showAuditLogs) {
      links.push({ label: 'Audit Logs', route: ['/audit-logs'] });
    }
    if (showPermSettings) {
      links.push({ label: 'Permission Settings', route: ['/permission-Settings'] });
    }
    if (showConfiguration) {
      links.push({ label: 'Configuration', route: ['/configuration'] });
    }

    this.navLinks = links;
    this.cdr.detectChanges();
  }
}
